import { forwardRef } from 'react';

import { CircleCheck, CircleX, LoaderCircle, TriangleAlert } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { DocProcessingErrorType, RunStatus } from '../../../../../../../../../../backend/src/types/types';
import { Icon } from '../../../../../../../components/icon';
import { useGetCase } from '@/api/queries/useGetCase';
import { Doc } from '@/api/types/backend-dto-types';
import { isProcessingStatus } from '@/constants/pipelineStatus';
import { cn } from '@/helpers/shadCnUtils';
import { Button } from '@/new/components/shadcn/button';

export const DocRow = forwardRef<
  HTMLDivElement,
  {
    doc: Doc & {
      hasKeyFacts: boolean;
      hasRelevantFacts: boolean;
      chunkMatchCount: number;
    };
    matterId: string;
    searchQuery?: string;
    isSelected?: boolean;
    onSelect?: () => void;
  }
>(({ doc, matterId, isSelected = false, onSelect }, ref) => {
  const { subsetId } = useParams();
  const navigate = useNavigate();
  const { data: caseData } = useGetCase(matterId);

  const isCaseReprocessing =
    caseData?.runs?.at(0) && caseData.runs.length > 1 && isProcessingStatus(caseData.runs[0].status as RunStatus);
  const hasFileError = doc.pipelineErrorType === DocProcessingErrorType.FileError;

  const processStates = {
    notProcessed: { icon: <CircleX size={14} />, text: 'Failed to Process' },
    unableToOpen: { icon: <TriangleAlert size={14} />, text: 'Unable to open' },
    processed: { icon: <CircleCheck size={14} className="text-green-500" />, text: 'Processed' },
    processing: {
      icon: <LoaderCircle size={14} className="animate-spin text-gray-500" />,
      text: 'Processing...',
    },
  };

  let processState = processStates.notProcessed;

  if (hasFileError) {
    processState = processStates.unableToOpen;
  } else if (doc.isProcessed) {
    processState = processStates.processed;
  } else if (isCaseReprocessing) {
    processState = processStates.processing;
  }

  return (
    <div
      ref={ref}
      className={cn(
        'group relative cursor-pointer scroll-my-8 rounded-md border border-gray-300 bg-white shadow-lg/2 transition-all duration-200 hover:shadow-lg/8 hover:shadow-teal-500',
        processState === processStates.notProcessed && 'border-red-200 bg-red-50/50',
        processState === processStates.unableToOpen && 'border-amber-200 bg-amber-50/50',
        isSelected && 'border-teal-400',
        !isSelected && 'hover:border-gray-300',
      )}
      onClick={onSelect}
    >
      <div className="flex items-center justify-between rounded-t-md border-b bg-gray-50 p-4 py-1.75 pr-1.5">
        <span className="flex items-center gap-2">
          <Icon
            className={cn(
              'lucide--file shrink-0 text-xs text-gray-400 group-hover:text-teal-500',
              isSelected && 'text-teal-500',
            )}
          />
          <h3 className="line-clamp-1 text-xs font-medium text-ellipsis text-gray-900/80" title={doc.file.name}>
            {doc.file.name}
          </h3>
        </span>

        <Button size="sm" onClick={() => navigate(`/matters/${matterId}/subsets/${subsetId}/docs/${doc.id}`)}>
          View
          <Icon className="iconify lucide--arrow-right" />
        </Button>
      </div>

      <div className="p-4 py-2 pb-2.5 text-xs">
        <div className="line-clamp-1">
          <span className="pr-1 font-medium text-gray-800/60">Author:</span>
          {doc.author ? (
            <span className="text-gray-900/80" title={doc.author}>
              {doc.author}
            </span>
          ) : (
            <span className="font-medium text-gray-500">N/A</span>
          )}
        </div>

        <div className="line-clamp-1">
          <span className="pr-1 font-medium text-gray-800/60">Date:</span>
          {doc.dateText ? (
            <span className="text-gray-900/80">{doc.dateText}</span>
          ) : (
            <span className="font-medium text-gray-500">N/A</span>
          )}
        </div>
      </div>
    </div>
  );
});

DocRow.displayName = 'DocRow';
